import { createSlice } from '@reduxjs/toolkit';
import NProgressModule from 'nprogress';
import { useEffect } from 'react';
import { sitecoreAxios } from '@pm2/api';
import { easings } from '@ui/css';
import { createIndependentStore, useLoaderState, useSelf } from '@utils/react';
import { bar, peg } from './nrogress.styles.js';

const NProgress = ({
  minDuration
}) => {
  const enabled = nProgressStore.useSelector(state => state.enabled);
  const showLoader = useLoaderState(enabled, {
    durationMs: minDuration
  });
  const self = useSelf({});
  useEffect(() => {
    if (showLoader.current) {
      if (self.startedAt === undefined) {
        self.startedAt = Date.now();
        NProgressModule.start();
      }
      const startedAt = self.startedAt;

      // when updating nprogress, make sure that we never exceed the lowest
      // value based on min duration as well as request progress
      const updateNProgress = () => {
        if (self.startedAt === undefined) {
          // bail out if page has already finished loading (that simply means
          // that other )
          return;
        }
        const elapsedTime = Date.now() - startedAt;
        const elapsedDuration = elapsedTime / minDuration;
        self.prev = Math.max(self.prev ?? 0, Math.min(self.requestProgress ?? 1, elapsedDuration) * 0.96);
        NProgressModule.set(self.prev);
      };

      // auto-update NProgress every 100ms based on duration progression
      window.clearInterval(self.updateInterval);
      self.updateInterval = window.setInterval(updateNProgress, 100);
      return bindAxiosRequestTracker((requestsMade, requestsCompleted) => {
        self.requestProgress = requestsCompleted / requestsMade;
        updateNProgress();
      });
    } else {
      window.clearInterval(self.updateInterval);
      NProgressModule.done();

      // reset internal state for next time the progressbar will be displayed
      self.startedAt = undefined;
      self.requestProgress = undefined;
      self.prev = undefined;
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [self, minDuration, showLoader.current]);
  return null;
};
let ONGOING_REQUESTS = 0;
const REQUEST_INTERCEPTORS = new Set();
const RESPONSE_INTERCEPTORS = new Set();

// bind the request interceptors immediately, as we need to be able to track
// ongoing requests prior to binding request trackers for correct statistics
// (in case requests are started shortly before we start tracking)
sitecoreAxios.interceptors.request.use(config => {
  ONGOING_REQUESTS++;
  REQUEST_INTERCEPTORS.forEach(interceptor => interceptor());
  return config;
});
sitecoreAxios.interceptors.response.use(config => {
  ONGOING_REQUESTS--;
  RESPONSE_INTERCEPTORS.forEach(interceptor => interceptor());
  return config;
}, err => {
  ONGOING_REQUESTS--;
  RESPONSE_INTERCEPTORS.forEach(interceptor => interceptor());
  return Promise.reject(err);
});
/**
 * binds a callback that'll be triggered every time requests are started and
 * completed through axios, making it easy to track progress.
 */
function bindAxiosRequestTracker(callback) {
  let requestsMade = ONGOING_REQUESTS;
  let requestsCompleted = 0;

  // add request and response interceptors to axios to keep notifying the
  // subscriber about progress
  const onRequest = () => {
    callback(++requestsMade, requestsCompleted);
  };
  const onResponse = () => {
    // defer response 100ms, just in case the client will trigger a new request
    // based on the response from this one (that way we don't accidentally
    // end up considering the progress being further ahead than will actually
    // be the case)
    setTimeout(() => callback(requestsMade, ++requestsCompleted), 100);
  };
  REQUEST_INTERCEPTORS.add(onRequest);
  RESPONSE_INTERCEPTORS.add(onResponse);
  if (requestsMade > 0) {
    callback(requestsMade, requestsCompleted);
  }
  return () => {
    REQUEST_INTERCEPTORS.delete(onRequest);
    RESPONSE_INTERCEPTORS.delete(onResponse);
  };
}
NProgressModule.configure({
  showSpinner: false,
  easing: easings.inOutQuad,
  speed: 500,
  template: `<div class="${bar}" role="bar"><div class="${peg}"></div></div>`
});
const nProgressStore = createIndependentStore(createSlice({
  name: "nProgress",
  initialState: {
    enabled: false
  },
  reducers: {
    enable(state) {
      state.enabled = true;
    },
    disable(state) {
      state.enabled = false;
    }
  }
}));

export { NProgress, nProgressStore };
//# sourceMappingURL=nprogress.js.map
